import React, { useCallback, useEffect, useState } from 'react';
import {
	Box,
	Divider,
	IconButton,
	InputAdornment,
	MenuItem,
	MenuItemProps,
    OutlinedInput,

 
} from '@mui/material';
import Select from '../select/select';
import Checkbox from '../checkbox/checkbox';
import Typography from '../typography/typography';
// import Chip from '../chip/chip'
import ChipOrPlaceholder from '../form-control/form-builder/form-builder-element/chip-or-placeholder';

import './multi-select.scss';
import { Add, Close } from '@mui/icons-material';
// import { MoreIcon } from '../icons';
import _ from 'lodash';
import { useTranslation } from "react-i18next";


interface MultiSelectProps {
  label?: string;
  placeholder?: string;
  value?: any[];
  onChange?: (value: any[]) => void;
  options?: Array<{ label: string; value: any }>;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
}

interface IMenuItems extends MenuItemProps {
	options: any[];
	multiple: boolean;
	placeholder: string;
	with_checkboxes: boolean;
	values: string | string[];
	field: any;
}

const generateMenuItems = ({
	options,
	multiple,
	placeholder,
	with_checkboxes,
	values,
	field,
}: IMenuItems) => {

	const items: React.ReactElement[] = [];
	if (!multiple) {
		items.push(
			<MenuItem disabled value=''>
				<Typography type='s3' color='theme.secondary.1000'>
					<em>{placeholder}</em>
				</Typography>
			</MenuItem>
		);
	}
	options?.forEach((option) => {
		
		items.push(
			<MenuItem
				value={option.value}
				key={option.value}
				disabled={Boolean(option.disabled)}
				className={option?.isGroup ? 'select--MenuItemHeader' : ''}>
				<Box sx={{ display: 'flex', gap: 1 }}>
					{multiple && with_checkboxes && (
						<Checkbox
							checked={_.some(values, option.value)}
						/>
					)}
					<Typography type='s3' color='theme.secondary.1000'>
						{option.label}
					</Typography>
				</Box>
			</MenuItem>
		);
		if (option?.isGroup && Array.isArray(option?.options)) {
			option.options.forEach((opt) => {
				items.push(
					<MenuItem
						value={opt.value}
						disabled={Boolean(opt.disabled)}
						sx={{ pl: 5 }}
						key={`${option.value}-${opt.value}`}>
						<Box sx={{ display: 'flex', gap: 1 }}>
							{multiple && with_checkboxes && (
								<Checkbox
									checked={Boolean(values && values?.indexOf(opt.value) > -1)}
								/>
							)}
							<Typography type='s3' color='theme.secondary.1000'>
								{opt.label}
							</Typography>
						</Box>
					</MenuItem>
				);
			});
		}
	});
	if (field.enable_footer) {
		items.push(
			<Box>
				<Divider flexItem />
				<MenuItem sx={{ gap: 1 }} onClick={field.onFooterClick}>
					<Add fontSize='small' />
					<Typography type='s4' color='theme.secondary.1000'>
						Create New {field.label}
					</Typography>
				</MenuItem>
			</Box>
		);
	}

	return items;
};

const SelectedItems = ({
	data,
	options
}: {
	data: string[];
	options: any[];
}) => {
	const displayTitle = data.map(
		(selectedItem) =>
			options?.find((option: any) => (option.value?.id || option.value?.name || option.value) === (selectedItem?.id || selectedItem?.name || selectedItem))?.label ||
			selectedItem
	);
	return (
		<Typography
			type='s4'
			color='inherit'
			sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5, position:'absolute' }}>
			{displayTitle.slice(0, 4).join(', ')}
			{data.length > 4 && (
				// <Chip variant='outlined' type='normal' label={<MoreIcon fontSize='small'/>} />
				'...'
			)}
		</Typography>
	);
};

const MultiSelect = (props: MultiSelectProps) => {
	const [options, setOptions] = useState([]);
	const [selectedValue, setSelectedValue] = useState(props.defaultValue || props.value || (props.is_multiselect ? [] : ''));

	const removeUndefine = (selectOptions: any[]) => {
		return selectOptions?.filter((option) => option);
	};

	const handleSetOptions = useCallback(() => {
		setOptions(props.options);
	}, [props.options]);

	useEffect(() => {
		handleSetOptions();
	}, [handleSetOptions]);

	const handleChange = (e: any, child) => {
        let value = e?.target?.value
				if(props.is_multiselect) {
					const sValue = child?.props?.value
					const ev = selectedValue.find(v => v?.name == sValue?.name)
					if(ev){
						value = selectedValue.filter(v => v?.name != sValue?.name)
					}
					value = value?.filter(Boolean)
				}
				setSelectedValue(value);
				if (props.onChange) {
					props.onChange(value);
				}
	};

	useEffect(() => {
		setSelectedValue(props.defaultValue || props.value || (props.is_multiselect ? [] : ''));
	}, [props.defaultValue, props.value, props.is_multiselect]);

		const { t } = useTranslation();

	return (
		<>
			<Select
				fullWidth
				placeholder={props.placeholder}
				label={props.label}
				value={selectedValue}
				defaultValue={props.defaultValue || (props.is_multiselect ? [] : '')}
				required={props.required}
				size={props.size || 'small'}
				multiple={Boolean(props?.is_multiselect)}
				disabled={Boolean(props?.disabled)}
				variant='outlined'
        // sx={{minWidth: '165px'}}
				sx={(Boolean(props?.is_multiselect) && selectedValue?.length) ? { "& .MuiSelect-select": { padding: '5.5px !important', minWidth: '165px' }} : { minWidth: '165px' }}
				className={
					props.is_multiselect && removeUndefine(selectedValue)?.length
						? 'MultiSelect'
						: ''
				}
				input={
						!props?.is_multiselect && !props.disabled ? (
								<OutlinedInput
										label=''
										endAdornment={
												<InputAdornment position='end'>
														{props.value && (
																<IconButton
																		aria-label='clear selection'
																		onClick={() => props.onChange(null)}
																		edge='end'
																		sx={{ mr: 1.5 }}
																		size='small'>
																		<Close sx={{ fontSize: '1rem' }} />
																</IconButton>
														)}
												</InputAdornment>
										}
								/>
						) : undefined
				}
				renderValue={(selected: string | string[]) => (
					<>
						{Array.isArray(selected) ? (
							<ChipOrPlaceholder
								selectedLabel={props.selectedLabel}
								data={removeUndefine(selected)}
								placeholder={props.placeholder}
								onDelete={props?.onChipRemove}
								disabled={props.disabled}
								chipProps={{
									sx: {
											bgcolor: 'theme.primary.100',
											border: 0,
											// borderRadius: '15px',
											'& .MuiTypography-body1': {
													color: 'theme.primary.800',
													fontWeight: "500",
											},
											'& .MuiChip-deleteIcon': {
												color: 'theme.primary.800',
												'&:hover':{
													color: 'theme.primary.900',
												}
											}
									}
							}}
							/>
						) : (
							options?.find(
								(option: any) =>
									(option.value?.id || option.value?.name || option.value) === (selected?.id || selected?.name || selected)
							)?.label ||
							selected ||
							props.placeholder
						)}
					</>
				)}
				searchPlaceholder={`${t('common.search')} ${props.label}`}
				handleSearch={props.handleSearch}
				searchValue={props.searchValue || ''}
				onChange={handleChange}
				loading={props?.is_loading}>
				{generateMenuItems({
					options,
					multiple: Boolean(props.is_multiselect),
					placeholder: props.placeholder,
					values: selectedValue,
					with_checkboxes: true,
					field: props
				})}
			</Select>
			{props.is_multiselect && Array.isArray(selectedValue) && (
				<SelectedItems data={selectedValue} options={options} />
			)}
		</>
	);
};
export { MultiSelect };
export default MultiSelect;
